<template>
  <el-dialog ref="dialog" :visible="visible" :title="'恢复' + kindLabel + '上一版'" width="min(600px, calc(100vw - 24px))" append-to-body :close-on-click-modal="false" :close-on-press-escape="!saving" :show-close="!saving" :before-close="close" @opened="focusCancel" @closed="$emit('closed')" @keydown.tab.native="trapFocus">
    <div class="template-restore-body" :aria-busy="String(loading || saving)">
      <p v-if="loading" role="status">正在读取上一版…</p>
      <template v-if="preview">
        <p class="template-restore-copy">当前“{{ preview.current.name }}”为 v{{ preview.current.version }}，将使用上一版“{{ preview.previous.name }}”（v{{ preview.previous.version }}）的内容，保存为 v{{ preview.current.version + 1 }}。</p>
        <div v-if="kind === 'project'" class="template-restore-pages">
          <label for="template-restore-page">上一版共 {{ previousPages.length }} 个页面，选择页面核对</label>
          <select id="template-restore-page" v-model="selectedPageId" :disabled="saving">
            <option v-for="page in previousPages" :key="page.id" :value="page.id">{{ page.name }}{{ page.id === preview.previous.snapshot.homePageId ? '（首页）' : '' }}</option>
          </select>
        </div>
        <template-preview v-if="previewSnapshot" :key="selectedPageId" :value="previewSnapshot" :height="180" />
        <p v-if="kind !== 'component' && previewSnapshot" class="template-restore-copy">画布 {{ previewConfig.width }} × {{ previewConfig.height }} · {{ previewConfig.fitType || '沿用默认适配' }}。预览仅展示静态样式，不执行接口、脚本或设备订阅。</p>
        <p class="template-restore-copy">恢复该版的名称、说明、分类和{{ kind === 'component' ? '样式' : kind === 'page' ? '页面配置与组件' : '全部页面、首页及内部跳转关系' }}；公开／私有保持当前范围。已有历史保留，{{ copyNotice }}保存前校验绑定的固定图片，图片版本或文件缺失时拒绝恢复。</p>
        <p v-if="preview.previous.description" class="template-restore-copy">{{ preview.previous.description }}</p>
        <p v-if="!canPublishCurrent" role="status">当前模板为公开模板，恢复也需要“公开模板”权限。</p>
      </template>
      <p v-if="error" class="template-restore-error" role="alert">{{ error }}</p>
      <el-button v-if="!preview && error && !loading" size="small" @click="load">重新读取上一版</el-button>
      <p v-if="saving" role="status">正在恢复，请勿重复提交…</p>
    </div>
    <span slot="footer" class="template-restore-actions"><el-button ref="cancel" :disabled="saving" @click="close">{{ blocked ? '关闭并刷新列表' : '取消' }}</el-button><el-button type="primary" :loading="saving" :disabled="!preview || loading || saving || blocked || !canSave || !canPublishCurrent" @click="restore">确认恢复上一版</el-button></span>
  </el-dialog>
</template>
<script>
import store from '@/store'
import * as templateApi from '@/api/templates'
import * as componentApi from '@/api/component-resources'
import { componentRestorePreview, restoredComponentResource } from '@/utils/component-resources'
import { templateRestorePreview, restoredTemplate, templateError } from '@/utils/templates'
import { createRequestCancellation, isRequestCanceled } from '@/api/http'
import TemplatePreview from './preview.vue'

export default {
  components: { TemplatePreview },
  props: { resourceMode: Boolean, visible: Boolean, templateId: { type: String, required: true }, kind: { type: String, default: 'component', validator: value => ['component', 'page', 'project'].includes(value) } },
  data: () => ({ preview: null, selectedPageId: '', loading: false, saving: false, blocked: false, error: '' }),
  computed: {
    api () { return this.resourceMode ? componentApi : templateApi },
    permission () { return this.resourceMode ? 'visualizationComponent' : 'visualizationTemplate' },
    kindLabel () { if (this.resourceMode) return '组件'; return { component: '组件模板', page: '页面模板', project: '项目模板' }[this.kind] },
    copyNotice () { return this.kind === 'component' ? '已拖入大屏的组件副本不会改变。' : this.kind === 'page' ? '已应用的页面及其组件副本不会改变。' : '由此模板创建的已有大屏不会改变，也不会覆盖当前编辑中的项目。' },
    previousPages () { return this.preview && this.kind === 'project' ? this.preview.previous.snapshot.pages : [] },
    previewConfig () {
      if (!this.previewSnapshot) return {}
      return this.kind === 'project' ? this.previousPages.find(page => page.id === this.selectedPageId).config : this.previewSnapshot.config || {}
    },
    previewSnapshot () {
      if (!this.preview) return null
      const snapshot = this.preview.previous.snapshot
      if (snapshot.kind !== 'project') return snapshot
      const page = snapshot.pages.find(item => item.id === this.selectedPageId)
      // 仅构造预览副本，不把页面选择写回恢复快照；跳转配置仍由完整项目版本保存。
      return page ? { schemaVersion: 1, kind: 'project', homePageId: page.id, pages: snapshot.pages } : null
    },
    canSave () { return store.getters['auth/hasResourcePermission'](this.permission + ':query') && store.getters['auth/hasResourcePermission'](this.permission + ':save') },
    canPublishCurrent () { return !this.preview || this.preview.current.visibility !== 'public' || store.getters['auth/hasResourcePermission'](this.permission + ':publish') }
  },
  watch: {
    visible (value) { if (value) this.load(); else this.cancelRead() },
    templateId () { if (this.visible) this.load() },
    kind () { if (this.visible) this.load() },
    canSave (value) { if (!value) { this.cancelRead(); this.$emit('update:visible', false) } }
  },
  created () { this._read = 0; if (this.visible) this.load() },
  mounted () { if (this.visible) this.$nextTick(this.focusCancel) },
  beforeDestroy () { this.cancelRead() },
  methods: {
    cancelRead () { this._read++; if (this._cancel) this._cancel.cancel() },
    focusCancel () { if (!this._isDestroyed && this.$refs.cancel) this.$refs.cancel.$el.focus() },
    trapFocus (event) {
      if (!this.$refs.dialog) return
      const controls = Array.from(this.$refs.dialog.$el.querySelectorAll('button:not([disabled]), select:not([disabled]), [tabindex="0"]')).filter(el => el.getClientRects().length)
      if (!controls.length) { event.preventDefault(); return }
      const first = controls[0], last = controls[controls.length - 1]
      if (event.shiftKey && event.target === first) { event.preventDefault(); last.focus() }
      else if (!event.shiftKey && event.target === last) { event.preventDefault(); first.focus() }
    },
    close () {
      if (this.saving) return
      this.cancelRead(); this.$emit('update:visible', false)
      if (this.blocked) this.$emit('check')
    },
    async load () {
      if (this.saving) return
      this.cancelRead(); const request = this._read, id = this.templateId, kind = this.kind
      this.preview = null; this.selectedPageId = ''; this.blocked = false; this.error = ''
      if (!this.canSave || !this.visible) return
      this._cancel = createRequestCancellation(); this.loading = true
      try {
        const preview = (this.resourceMode ? componentRestorePreview : templateRestorePreview)(await this.api.getPreviousTemplate(id, { cancelToken: this._cancel.token }), id, kind)
        if (request !== this._read || this._isDestroyed || !this.visible || !this.canSave || id !== this.templateId || kind !== this.kind) return
        this.preview = preview; this.selectedPageId = kind === 'project' ? preview.previous.snapshot.homePageId : ''
      } catch (error) {
        if (request === this._read && !this._isDestroyed && this.visible && this.canSave && id === this.templateId && kind === this.kind && !isRequestCanceled(error)) this.error = error.status === 404 ? '模板不可访问或恢复接口尚未部署，请刷新列表并确认已更新后端。' : templateError(error)
      } finally { if (request === this._read && !this._isDestroyed) this.loading = false }
    },
    async restore () {
      if (!this.preview || this.preview.current.kind !== this.kind || this.preview.current.id !== this.templateId || this.loading || this.saving || this.blocked || !this.visible || !this.canSave || !this.canPublishCurrent) return
      const preview = this.preview, id = this.templateId, kind = this.kind, request = this._read
      this.saving = true; this.error = ''
      try {
        const response = await this.api.restoreTemplate(id, { revision: preview.current.revision, version: preview.previous.version })
        this.blocked = true
        const item = (this.resourceMode ? restoredComponentResource : restoredTemplate)(response, preview)
        if (request !== this._read || this._isDestroyed || id !== this.templateId || kind !== this.kind || !this.visible || !this.canSave || !this.canPublishCurrent) return
        this.blocked = false; this.$emit('restored', item); this.$emit('update:visible', false)
      } catch (error) {
        if (request !== this._read || this._isDestroyed || id !== this.templateId || kind !== this.kind || !this.visible || !this.canSave) return
        // 冲突或回执不确定时不自动重发；关闭后刷新，重新读取最新的上一版。
        this.blocked = this.blocked || !error.status || error.status >= 500 || [401, 403, 404, 409].includes(error.status)
        this.error = this.blocked ? (error.status === 409 ? '模板已变化或历史不可用，本次未确认恢复；请关闭并刷新列表重新核对。' : '恢复结果或权限未确认，请关闭并刷新列表检查，勿重复提交。') : templateError(error)
        if (this.blocked) this.$nextTick(this.focusCancel)
      } finally { if (!this._isDestroyed) this.saving = false }
    }
  }
}
</script>
<style scoped>
.template-restore-body { max-height: 50vh; overflow-y: auto; overscroll-behavior: contain; color: #c9d5e2; scrollbar-width: none; -ms-overflow-style: none; }
.template-restore-body::-webkit-scrollbar { display: none; }
.template-restore-copy, .template-restore-error { white-space: pre-wrap; overflow-wrap: anywhere; line-height: 1.7; }
.template-restore-error { color: #ef7d7d; }
.template-restore-pages { display: grid; gap: 8px; margin-bottom: 12px; }
.template-restore-pages select { width: 100%; min-height: 44px; padding: 8px 12px; border: 1px solid #365064; border-radius: 4px; background: #102634; color: inherit; }
.template-restore-pages select:focus-visible { outline: 2px solid #409eff; outline-offset: 2px; }
.template-restore-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 8px; padding-bottom: env(safe-area-inset-bottom); }
.template-restore-actions .el-button { min-height: 44px; margin-left: 0; }
</style>
